/**
 * **实际牌数 → 渲染牌数。** 抽牌堆那一叠牌背用这个。
 *
 * 评审：「为性能考虑，不要抽牌堆有多少牌就渲染多少牌背，抽牌堆渲染数量和实际
 * 数量的映射关系有一个 `docs/archive/randerCount.ts` 给你参考。」
 *
 * 那份参考稿抄在这里当实现（原来放在 `docs/` 下，但它**是被 import 的代码**，
 * 不是文档 —— 放在 `src/render/game/` 下才跟着类型检查、才有测试）。
 *
 * ## 映射长什么样
 *
 * ```
 *   1 张  → 1 张
 *   10 张 → 10 张         ← exactThreshold：最后几张**必须**精确
 *   20 张 → 15 张
 *   40 张 → 20 张
 *   56 张 → 24 张         ← 趋近 maxRenderCount
 * ```
 *
 * 两条形状是**故意**的：
 *
 * - 小数量精确：牌快没了的时候，"还看得见几张"是玩家要读的信息
 * - 大数量饱和：多出来的那几十张在视觉上是同一坨 —— 第 30 张和第 56 张
 *   之间只差几个像素，画出来只是白白吃 GPU
 *
 * ⚠️ 结果**单调不减**而且永远 `<= actualCount`：前者保证"抽一张就少一张"
 * 这个手感不跳，后者保证不会画出比实际更多的牌。
 */

export interface RenderCountOptions {
  /** 实际数量不超过该值时，渲染数量 = 实际数量，保证最后几张不穿帮 */
  exactThreshold?: number;
  /** 渲染数量的上限，避免大数量时渲染压力过大 */
  maxRenderCount?: number;
  /** 压缩曲线，越小前期越接近线性，越大压缩越狠 */
  curve?: number;
}

export const RENDER_COUNT_DEFAULTS = {
  exactThreshold: 10,
  maxRenderCount: 30,
  curve: 1,
} as const;

/**
 * 将实际牌数映射为渲染牌数。
 *
 * - 小数量：完全按实际数量渲染，抽到最后几张不会穿帮
 * - 大数量：用饱和曲线压缩，渲染数量趋近 `maxRenderCount`
 * - 结果单调不减，且始终 `<= actualCount`
 */
export function getRenderCount(
  actualCount: number,
  options: RenderCountOptions = {},
): number {
  const {
    exactThreshold = RENDER_COUNT_DEFAULTS.exactThreshold,
    maxRenderCount = RENDER_COUNT_DEFAULTS.maxRenderCount,
    curve = RENDER_COUNT_DEFAULTS.curve,
  } = options;

  // 非法输入兜底
  if (!Number.isFinite(actualCount) || actualCount <= 0) return 0;

  const count = Math.floor(actualCount);

  // 小数量：精确渲染
  if (count <= exactThreshold) return count;

  // 大数量：饱和压缩
  const extraActual = count - exactThreshold;
  const extraBudget = Math.max(maxRenderCount - exactThreshold, 0);

  if (extraBudget === 0) return Math.min(exactThreshold, count);

  const scaled = extraBudget * (extraActual / (extraActual + extraBudget * curve));
  const renderCount = exactThreshold + Math.ceil(scaled);

  return Math.min(renderCount, count);
}

/** 现在这一叠是不是"最后几张"（牌背之间的上移可以稍微大一点）。见 `drawPile.ts` */
export function isNearlyEmpty(
  actualCount: number,
  options: RenderCountOptions = {},
): boolean {
  const threshold = options.exactThreshold ?? RENDER_COUNT_DEFAULTS.exactThreshold;
  return Number.isFinite(actualCount) && actualCount > 0 && Math.floor(actualCount) <= threshold;
}
